import { collection, addDoc, query, where, onSnapshot, updateDoc, doc, orderBy, limit, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { Notification, NotificationType, NotificationPriority } from '../types/notifications';

export class NotificationService {
  static async createNotification(notification: Omit<Notification, 'id' | 'createdAt'>) {
    try {
      const docRef = await addDoc(collection(db, 'notifications'), {
        ...notification,
        createdAt: new Date().toISOString(),
        read: false
      });
      
      // Send desktop notification if permission granted
      if (notification.priority === 'high' && 'Notification' in window && window.Notification.permission === 'granted') {
        await this.sendDesktopNotification(notification.title, notification.message, notification.actionUrl);
      }
      
      return docRef.id;
    } catch (error) {
      console.error('Error creating notification:', error);
      throw error;
    }
  }

  static subscribeToNotifications(userId: string, callback: (notifications: Notification[]) => void) {
    const q = query(
      collection(db, 'notifications'),
      where('recipientId', '==', userId),
      orderBy('createdAt', 'desc'),
      limit(50)
    );

    return onSnapshot(q, (snapshot) => {
      const notifications: Notification[] = [];
      snapshot.forEach((doc) => {
        const data = doc.data();
        notifications.push({
          id: doc.id,
          ...data,
          createdAt: data.createdAt || new Date().toISOString()
        } as Notification);
      });
      callback(notifications);
    });
  }

  static async markAsRead(notificationId: string) {
    try {
      await updateDoc(doc(db, 'notifications', notificationId), {
        read: true
      });
    } catch (error) {
      console.error('Error marking notification as read:', error);
      throw error;
    }
  }

  static async requestNotificationPermission(): Promise<boolean> {
    if (!('Notification' in window)) {
      console.log('This browser does not support desktop notifications');
      return false;
    }

    if (window.Notification.permission === 'granted') {
      return true;
    }

    if (window.Notification.permission !== 'denied') {
      const permission = await window.Notification.requestPermission();
      return permission === 'granted';
    }

    return false;
  }

  // Helper methods for specific notification types
  static async createTaskNotification(
    taskTitle: string, 
    assignedBy: string, 
    assignedToUserId: string, 
    priority: 'low' | 'medium' | 'high',
    dueDate?: string
  ) {
    const isHighPriority = priority === 'high';
    const title = isHighPriority ? `🚨 Urgent: New High-Priority Task Assigned` : `📋 New Task Assigned`;
    const dueDateText = dueDate ? ` (Due: ${new Date(dueDate).toLocaleDateString()})` : '';
    
    return this.createNotification({
      type: 'task',
      title,
      message: `${assignedBy} assigned you the task: "${taskTitle}"${dueDateText}`,
      priority,
      recipientId: assignedToUserId,
      actionUrl: '/tasks',
      read: false,
      metadata: {
        taskTitle,
        assignedBy,
        dueDate: dueDate || null,
        taskPriority: priority
      }
    });
  }

  static async createMessageNotification(
    senderName: string,
    messageContent: string,
    recipientId: string,
    channelName?: string
  ) {
    const title = channelName ? `New message in #${channelName}` : 'New direct message';
    const preview = messageContent.length > 50 ? `${messageContent.substring(0, 50)}...` : messageContent;
    
    return this.createNotification({
      type: 'message',
      title,
      message: `${senderName}: ${preview}`,
      priority: 'medium',
      recipientId,
      actionUrl: '/messages',
      read: false,
      metadata: {
        senderName,
        channelName: channelName || null,
        messagePreview: preview
      }
    });
  }

  static async createEventNotification(
    eventTitle: string,
    eventDate: string,
    recipientId: string,
    reminderMinutes: number = 30
  ) {
    const eventDateTime = new Date(eventDate);
    const reminderText = reminderMinutes === 1440 ? '1 day' : 
                        reminderMinutes === 60 ? '1 hour' : 
                        `${reminderMinutes} minutes`;
    
    return this.createNotification({
      type: 'reminder',
      title: `📅 Event Reminder`,
      message: `"${eventTitle}" starts in ${reminderText} (${eventDateTime.toLocaleString()})`,
      priority: 'medium',
      recipientId,
      actionUrl: '/calendar',
      read: false,
      metadata: {
        eventTitle,
        eventDate,
        reminderMinutes
      }
    });
  }

  static async sendDesktopNotification(title: string, message: string, actionUrl?: string): Promise<void> {
    if (!('Notification' in window) || window.Notification.permission !== 'granted') {
      return;
    }

    const notification = new window.Notification(title, {
      body: message,
      icon: '/icons/icon-192x192.png',
      badge: '/icons/icon-72x72.png',
      tag: 'schoolsync-notification',
      requireInteraction: true,
    });

    notification.onclick = () => {
      window.focus();
      if (actionUrl) {
        window.location.href = actionUrl;
      }
      notification.close();
    };

    // Auto-close after 10 seconds
    setTimeout(() => {
      notification.close();
    }, 10000);
  }

  static async scheduleNotification(notification: Omit<Notification, 'id' | 'createdAt'>, delayMs: number): Promise<string> {
    return new Promise((resolve) => {
      setTimeout(async () => {
        const id = await this.createNotification(notification);
        resolve(id);
      }, delayMs);
    });
  }

  static async sendBulkNotifications(notifications: Omit<Notification, 'id' | 'createdAt'>[]): Promise<string[]> {
    const ids = await Promise.all(
      notifications.map(notification => this.createNotification(notification))
    );
    return ids;
  }

  static registerServiceWorker(): void {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.register('/sw.js')
        .then((registration) => {
          console.log('Service Worker registered successfully:', registration);
          
          // Handle push notifications
          registration.addEventListener('updatefound', () => {
            const newWorker = registration.installing;
            if (newWorker) {
              newWorker.addEventListener('statechange', () => {
                if (newWorker.state === 'installed') {
                  console.log('New service worker installed');
                }
              });
            }
          });
        })
        .catch((error) => {
          console.error('Service Worker registration failed:', error);
        });
    }
  }

  static async subscribeToPushNotifications(): Promise<PushSubscription | null> {
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
      console.log('Push notifications not supported');
      return null;
    }

    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: this.urlBase64ToUint8Array(
          'your-vapid-public-key-here' // Replace with your VAPID public key
        )
      });

      console.log('Push subscription created:', subscription);
      return subscription;
    } catch (error) {
      console.error('Error subscribing to push notifications:', error);
      return null;
    }
  }

  private static urlBase64ToUint8Array(base64String: string): Uint8Array {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding)
      .replace(/-/g, '+')
      .replace(/_/g, '/');

    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);

    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  }
}

// Legacy compatibility
export const notificationService = {
  initialize: NotificationService.requestNotificationPermission,
  startListening: () => NotificationService.registerServiceWorker(),
  stopListening: () => console.log('Notification service stopped')
};